import * as Data from "effect/Data";

/**
 * Client for the hosted Roblox platform control plane (the Cloudflare Worker
 * that serves this bundle). Every route is same-origin and authenticated with
 * the current Clerk session token, which the caller supplies through
 * `readClerkToken` so this module never reaches into React or Clerk directly.
 *
 * Response parsing is deliberately concentrated here: the Worker is being
 * written in parallel, so a shape change should only ever touch this file.
 */

export const HOSTED_API_BASE_PATH = "/api";

export type HostedApiErrorCode =
  | "unauthenticated"
  | "owner_only"
  | "not_found"
  | "capacity_exceeded"
  | "unavailable"
  | "invalid_request"
  | "network"
  | "malformed_response"
  | "server_error";

export class HostedApiError extends Data.TaggedError("HostedApiError")<{
  readonly code: HostedApiErrorCode;
  readonly message: string;
  readonly status: number | null;
  readonly cause?: unknown;
}> {}

export interface HostedProject {
  readonly id: string;
  readonly name: string;
  readonly slug: string | null;
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

export type HostedStudioConnectionStatus = "connected" | "offline" | "never-paired";

export interface HostedStudioStatus {
  readonly status: HostedStudioConnectionStatus;
  readonly lastSeenAt: string | null;
  readonly placeId: string | null;
  readonly placeName: string | null;
  readonly deviceLabel: string | null;
}

export interface HostedPairingCode {
  readonly code: string;
  readonly expiresAt: string | null;
}

export type HostedProviderStatus = "connected" | "disconnected" | "unavailable";

export interface HostedProviderState {
  readonly provider: string;
  readonly status: HostedProviderStatus;
  readonly detail: string | null;
  readonly models: ReadonlyArray<string>;
  readonly accountLabel: string | null;
}

export type HostedMemoryScope = "user" | "project";

export interface HostedMemoryDocument {
  readonly scope: HostedMemoryScope;
  readonly text: string;
  readonly limit: number;
  /** Server-side character count; may differ from `text.length` for astral characters. */
  readonly used: number;
  readonly version: number;
  readonly updatedAt: string | null;
}

/**
 * Memory mutations are substring operations rather than whole-document writes,
 * which is what lets the Worker reject ambiguous edits. A full-text editor maps
 * onto them by replacing the entire previous document.
 */
export type HostedMemoryOperation =
  | { readonly operation: "add"; readonly value: string }
  | { readonly operation: "replace"; readonly match: string; readonly replacement: string }
  | { readonly operation: "remove"; readonly match: string };

/**
 * Turns "the editor now holds this text" into one memory operation. Returns
 * null when nothing changed, so an unchanged save never reaches the Worker.
 */
export function resolveMemoryReplacementOperation(input: {
  readonly previous: string;
  readonly next: string;
}): HostedMemoryOperation | null {
  if (input.previous === input.next) {
    return null;
  }
  if (input.previous.length === 0) {
    return { operation: "add", value: input.next };
  }
  if (input.next.length === 0) {
    return { operation: "remove", match: input.previous };
  }
  return { operation: "replace", match: input.previous, replacement: input.next };
}

/**
 * Character caps from the private alpha plan. The Worker is authoritative and
 * may report a different `limit`; these are the fallbacks used before a
 * document loads so the editor can show a budget immediately.
 */
export const HOSTED_MEMORY_LIMITS: Readonly<Record<HostedMemoryScope, number>> = {
  user: 1_500,
  project: 3_000,
};

export interface HostedApiClient {
  readonly listProjects: () => Promise<ReadonlyArray<HostedProject>>;
  readonly createProject: (input: { readonly name: string }) => Promise<HostedProject>;
  readonly getProject: (projectId: string) => Promise<HostedProject>;
  readonly getStudioStatus: (projectId: string) => Promise<HostedStudioStatus>;
  readonly createPairingCode: (projectId: string) => Promise<HostedPairingCode>;
  /**
   * ChatGPT readiness. Codex and Claude availability is derived from a linked
   * local environment, not from the Worker, so only the hosted provider needs a
   * backend read here.
   */
  readonly readChatGptStatus: () => Promise<HostedProviderState>;
  /** User memory is account-scoped; project memory needs a project id. */
  readonly readMemory: (input: {
    readonly scope: HostedMemoryScope;
    readonly projectId: string | null;
  }) => Promise<HostedMemoryDocument>;
  readonly applyMemoryOperation: (input: {
    readonly scope: HostedMemoryScope;
    readonly projectId: string | null;
    readonly operation: HostedMemoryOperation;
  }) => Promise<HostedMemoryDocument>;
}

export interface HostedApiClientOptions {
  readonly readClerkToken: () => Promise<string | null>;
  readonly basePath?: string;
  readonly fetch?: typeof globalThis.fetch;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(source: Record<string, unknown> | null, key: string): string | null {
  const value = source?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function readStringArray(source: Record<string, unknown> | null, key: string): string[] {
  const value = source?.[key];
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function readNumber(source: Record<string, unknown> | null, key: string): number | null {
  const value = source?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function statusToErrorCode(status: number): HostedApiErrorCode {
  switch (status) {
    case 400:
      return "invalid_request";
    case 401:
      return "unauthenticated";
    case 403:
      return "owner_only";
    case 404:
      return "not_found";
    case 409:
    case 413:
      return "capacity_exceeded";
    case 501:
    case 503:
      return "unavailable";
    default:
      return "server_error";
  }
}

const ERROR_CODES_BY_NAME: Readonly<Record<string, HostedApiErrorCode>> = {
  unauthenticated: "unauthenticated",
  unauthorized: "unauthenticated",
  authentication_required: "unauthenticated",
  invalid_session: "unauthenticated",
  owner_only: "owner_only",
  forbidden: "owner_only",
  owner_not_configured: "unavailable",
  clerk_not_configured: "unavailable",
  not_found: "not_found",
  project_not_found: "not_found",
  studio_session_not_found: "not_found",
  capacity_exceeded: "capacity_exceeded",
  memory_capacity_exceeded: "capacity_exceeded",
  request_too_large: "capacity_exceeded",
  unavailable: "unavailable",
  provider_unavailable: "unavailable",
  chatgpt_unavailable: "unavailable",
  invalid_request: "invalid_request",
  invalid_project_name: "invalid_request",
  invalid_memory_operation: "invalid_request",
  invalid_memory_value: "invalid_request",
  invalid_memory_match: "invalid_request",
  memory_substring_not_found: "invalid_request",
  memory_substring_not_unique: "invalid_request",
};

/**
 * Normalizes an error body into a code plus message. The Worker is expected to
 * answer `{ error: { code, message } }`, but a bare `{ error: "owner_only" }`
 * or a plain-text body still has to produce a usable message.
 */
export function parseHostedApiErrorBody(input: {
  readonly status: number;
  readonly body: unknown;
}): { readonly code: HostedApiErrorCode; readonly message: string } {
  const fallbackCode = statusToErrorCode(input.status);
  const root = asRecord(input.body);
  const errorValue = root?.["error"];
  const errorRecord = asRecord(errorValue);
  const rawCode =
    readString(errorRecord, "code") ??
    (typeof errorValue === "string" ? errorValue : null) ??
    readString(root, "code");
  const code = (rawCode && ERROR_CODES_BY_NAME[rawCode]) || fallbackCode;
  const message =
    readString(errorRecord, "message") ??
    readString(root, "message") ??
    (typeof input.body === "string" && input.body.trim().length > 0 ? input.body.trim() : null) ??
    defaultHostedApiErrorMessage(code);
  return { code, message };
}

export function defaultHostedApiErrorMessage(code: HostedApiErrorCode): string {
  switch (code) {
    case "unauthenticated":
      return "Sign in to continue.";
    case "owner_only":
      return "This account is not part of the private alpha.";
    case "not_found":
      return "That project no longer exists.";
    case "capacity_exceeded":
      return "That change is over the allowed capacity.";
    case "unavailable":
      return "That feature is not available yet.";
    case "invalid_request":
      return "The request was rejected.";
    case "network":
      return "Could not reach the server.";
    case "malformed_response":
      return "The server returned an unexpected response.";
    case "server_error":
      return "The server could not complete the request.";
  }
}

export function parseHostedProject(value: unknown): HostedProject | null {
  const record = asRecord(value);
  const id = readString(record, "id") ?? readString(record, "projectId");
  const name = readString(record, "name") ?? readString(record, "title");
  if (!id || !name) {
    return null;
  }
  return {
    id,
    name,
    slug: readString(record, "slug"),
    createdAt: readString(record, "createdAt") ?? readString(record, "created_at"),
    updatedAt: readString(record, "updatedAt") ?? readString(record, "updated_at"),
  };
}

export function parseHostedProjectList(value: unknown): ReadonlyArray<HostedProject> {
  const root = asRecord(value);
  const list = Array.isArray(value)
    ? value
    : Array.isArray(root?.["projects"])
      ? root["projects"]
      : null;
  if (!list) {
    return [];
  }
  const projects: HostedProject[] = [];
  for (const entry of list) {
    const project = parseHostedProject(entry);
    if (project) {
      projects.push(project);
    }
  }
  return projects;
}

export function parseHostedStudioStatus(value: unknown): HostedStudioStatus {
  const root = asRecord(value);
  // `GET /api/projects/:id` answers `{ project, studio }`, where `studio` is
  // null until the first pairing. A null studio is "never paired", not an
  // absent response.
  const hasStudioKey = root !== null && "studio" in root;
  const record = hasStudioKey ? asRecord(root["studio"]) : root;
  if (hasStudioKey && record === null) {
    return {
      status: "never-paired",
      lastSeenAt: null,
      placeId: null,
      placeName: null,
      deviceLabel: null,
    };
  }
  const lastSeenAt = readString(record, "lastSeenAt") ?? readString(record, "last_seen_at");
  const rawStatus = readString(record, "status");
  const connectedFlag = record?.["connected"];
  const status: HostedStudioConnectionStatus =
    rawStatus === "connected" || connectedFlag === true
      ? "connected"
      : rawStatus !== null || lastSeenAt !== null
        ? "offline"
        : "never-paired";

  return {
    status,
    lastSeenAt,
    placeId: readString(record, "placeId") ?? readString(record, "place_id"),
    placeName: readString(record, "placeName") ?? readString(record, "place_name"),
    deviceLabel:
      readString(record, "deviceLabel") ??
      readString(record, "device_label") ??
      readString(record, "deviceId"),
  };
}

export function parseHostedPairingCode(value: unknown): HostedPairingCode | null {
  const root = asRecord(value);
  const record = asRecord(root?.["pairing"]) ?? asRecord(root?.["pairingCode"]) ?? root;
  const code = readString(record, "code") ?? readString(record, "pairingCode");
  if (!code) {
    return null;
  }
  return {
    code,
    expiresAt: readString(record, "expiresAt") ?? readString(record, "expires_at"),
  };
}

/**
 * Reads the Login with ChatGPT handler's `/session` answer, which reports
 * `{ status: "authenticated" | "unauthenticated" | ..., user? }`.
 */
export function parseHostedChatGptStatus(value: unknown): HostedProviderState {
  const root = asRecord(value);
  const rawStatus = readString(root, "status");
  const user = asRecord(root?.["user"]);
  return {
    provider: "chatgpt",
    status: rawStatus === "authenticated" ? "connected" : "disconnected",
    detail: rawStatus === "expired" ? "The previous ChatGPT session expired." : null,
    models: readStringArray(root, "models"),
    accountLabel: readString(user, "email") ?? readString(user, "name"),
  };
}

function parseHostedMemoryDocument(scope: HostedMemoryScope, value: unknown): HostedMemoryDocument {
  const record = asRecord(value);
  const rawText = record?.["content"] ?? record?.["text"];
  const text = typeof rawText === "string" ? rawText : "";
  const limit = readNumber(record, "capacity") ?? readNumber(record, "limit");
  return {
    scope,
    text,
    limit: limit && limit > 0 ? limit : HOSTED_MEMORY_LIMITS[scope],
    used: readNumber(record, "usage") ?? [...text].length,
    version: readNumber(record, "version") ?? 0,
    updatedAt: readString(record, "updatedAt") ?? readString(record, "updated_at"),
  };
}

/**
 * Both memory routes answer one scoped document (`{ scope, content, version,
 * usage, capacity, updatedAt }`), optionally wrapped in `{ memory }`.
 */
export function parseHostedMemoryDocumentResponse(
  scope: HostedMemoryScope,
  value: unknown,
): HostedMemoryDocument {
  const root = asRecord(value);
  const container = asRecord(root?.["memory"]) ?? root;
  return parseHostedMemoryDocument(scope, container);
}

export function createHostedApiClient(options: HostedApiClientOptions): HostedApiClient {
  const basePath = (options.basePath ?? HOSTED_API_BASE_PATH).replace(/\/+$/, "");
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  async function call(
    path: string,
    init?: { readonly method?: string; readonly body?: unknown },
  ): Promise<unknown> {
    const token = await options.readClerkToken().catch(() => null);
    if (!token) {
      throw new HostedApiError({
        code: "unauthenticated",
        message: defaultHostedApiErrorMessage("unauthenticated"),
        status: null,
      });
    }

    const headers: Record<string, string> = {
      accept: "application/json",
      authorization: `Bearer ${token}`,
    };
    if (init?.body !== undefined) {
      headers["content-type"] = "application/json";
    }

    let response: Response;
    try {
      response = await doFetch(`${basePath}${path}`, {
        method: init?.method ?? "GET",
        headers,
        ...(init?.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      });
    } catch (cause) {
      throw new HostedApiError({
        code: "network",
        message: defaultHostedApiErrorMessage("network"),
        status: null,
        cause,
      });
    }

    const raw = await response.text().catch(() => "");
    let parsed: unknown = null;
    if (raw.trim().length > 0) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = raw;
      }
    }

    if (!response.ok) {
      const { code, message } = parseHostedApiErrorBody({ status: response.status, body: parsed });
      throw new HostedApiError({ code, message, status: response.status });
    }

    return parsed;
  }

  function malformed(message: string): HostedApiError {
    return new HostedApiError({ code: "malformed_response", message, status: null });
  }

  return {
    listProjects: async () => parseHostedProjectList(await call("/projects")),
    createProject: async ({ name }) => {
      const project = parseHostedProject(
        unwrapProject(await call("/projects", { method: "POST", body: { name } })),
      );
      if (!project) {
        throw malformed("The project was created but could not be read back.");
      }
      return project;
    },
    getProject: async (projectId) => {
      const project = parseHostedProject(
        unwrapProject(await call(`/projects/${encodeURIComponent(projectId)}`)),
      );
      if (!project) {
        throw malformed("That project could not be read.");
      }
      return project;
    },
    getStudioStatus: async (projectId) =>
      // Studio state rides along with the project read rather than a separate
      // route, so one request answers both.
      parseHostedStudioStatus(await call(`/projects/${encodeURIComponent(projectId)}`)),
    createPairingCode: async (projectId) => {
      const pairing = parseHostedPairingCode(
        await call(`/projects/${encodeURIComponent(projectId)}/studio/pairing-codes`, {
          method: "POST",
          body: {},
        }),
      );
      if (!pairing) {
        throw malformed("The server did not return a pairing code.");
      }
      return pairing;
    },
    readChatGptStatus: async () => {
      // The ChatGPT handler is mounted by the Worker only when its secret is
      // configured; a 503 is how "unavailable" is reported, and a 401 simply
      // means no ChatGPT session yet.
      try {
        return parseHostedChatGptStatus(await call("/chatgpt/session"));
      } catch (error) {
        if (error instanceof HostedApiError && error.code === "unavailable") {
          return {
            provider: "chatgpt",
            status: "unavailable",
            detail: error.message,
            models: [],
            accountLabel: null,
          };
        }
        if (
          error instanceof HostedApiError &&
          (error.status === 401 || error.code === "unauthenticated")
        ) {
          return {
            provider: "chatgpt",
            status: "disconnected",
            detail: null,
            models: [],
            accountLabel: null,
          };
        }
        throw error;
      }
    },
    readMemory: async ({ scope, projectId }) =>
      parseHostedMemoryDocumentResponse(scope, await call(memoryPath(scope, projectId))),
    applyMemoryOperation: async ({ scope, projectId, operation }) =>
      parseHostedMemoryDocumentResponse(
        scope,
        await call(memoryPath(scope, projectId), { method: "POST", body: operation }),
      ),
  };
}

function memoryPath(scope: HostedMemoryScope, projectId: string | null): string {
  if (scope === "user") {
    return "/memory/user";
  }
  if (!projectId) {
    throw new HostedApiError({
      code: "invalid_request",
      message: "Select a project before editing project memory.",
      status: null,
    });
  }
  return `/projects/${encodeURIComponent(projectId)}/memory`;
}

function unwrapProject(value: unknown): unknown {
  const record = asRecord(value);
  return record && "project" in record ? record["project"] : value;
}
